import { useCallback, useEffect, useState } from 'react'
import { token } from './anmeldung'

/** Ein Problembericht nach RFC 9457, wie ihn die Anwendung liefert. */
export interface Problembericht {
  type?: string
  title?: string
  status?: number
  detail?: string
  beanstandungen?: string[]
}

export class ApiFehler extends Error {
  constructor(
    readonly status: number,
    readonly bericht: Problembericht,
  ) {
    super(bericht.detail ?? bericht.title ?? `Fehler ${status}`)
  }
}

const basis = import.meta.env.VITE_API_BASIS ?? ''

async function anfrage<T>(pfad: string, einstellungen: RequestInit = {}): Promise<T> {
  const kopf = new Headers(einstellungen.headers)
  kopf.set('Accept', 'application/json')
  const zugriffstoken = token()
  if (zugriffstoken) {
    kopf.set('Authorization', `Bearer ${zugriffstoken}`)
  }
  if (einstellungen.body && !kopf.has('Content-Type')) {
    kopf.set('Content-Type', 'application/json')
  }

  const antwort = await fetch(`${basis}${pfad}`, { ...einstellungen, headers: kopf })
  if (!antwort.ok) {
    let bericht: Problembericht = { title: antwort.statusText }
    try {
      bericht = await antwort.json()
    } catch {
      /* Der Fehler kam ohne Problembericht - dann bleibt es beim Statustext. */
    }
    throw new ApiFehler(antwort.status, bericht)
  }
  if (antwort.status === 204) {
    return undefined as T
  }
  return (await antwort.json()) as T
}

export const api = {
  lies: <T>(pfad: string) => anfrage<T>(pfad),
  sende: <T>(pfad: string, inhalt: unknown) =>
    anfrage<T>(pfad, { method: 'POST', body: JSON.stringify(inhalt) }),
  aendere: <T>(pfad: string, inhalt: unknown) =>
    anfrage<T>(pfad, { method: 'PATCH', body: JSON.stringify(inhalt) }),
}

/** Eine geblaetterte Trefferliste, wie sie die Anwendung liefert. */
export interface Seite<T> {
  inhalt: T[]
  seite: number
  groesse: number
  gesamtzahl: number
  seitenzahl: number
}

export interface Abrufzustand<T> {
  daten?: T
  laeuft: boolean
  fehler?: ApiFehler
  erneut: () => void
}

/**
 * Laedt Daten und haelt Ladezustand und Fehler fest.
 *
 * Bewusst ohne Zwischenspeicher: die Angaben aendern sich waehrend der
 * Bearbeitung, und eine veraltete Rechnung in der Anzeige waere schlimmer als
 * eine zweite Anfrage.
 */
export function useAbruf<T>(pfad: string | undefined, abhaengig: unknown[] = []): Abrufzustand<T> {
  const [daten, setzeDaten] = useState<T>()
  const [laeuft, setzeLaeuft] = useState(false)
  const [fehler, setzeFehler] = useState<ApiFehler>()
  const [zaehler, setzeZaehler] = useState(0)

  const erneut = useCallback(() => setzeZaehler((wert) => wert + 1), [])

  useEffect(() => {
    if (!pfad) {
      return
    }
    let abgebrochen = false
    setzeLaeuft(true)
    setzeFehler(undefined)
    api
      .lies<T>(pfad)
      .then((ergebnis) => {
        if (!abgebrochen) {
          setzeDaten(ergebnis)
        }
      })
      .catch((ursache) => {
        if (!abgebrochen) {
          setzeFehler(
            ursache instanceof ApiFehler
              ? ursache
              : new ApiFehler(0, { detail: String(ursache) }),
          )
        }
      })
      .finally(() => {
        if (!abgebrochen) {
          setzeLaeuft(false)
        }
      })
    return () => {
      abgebrochen = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pfad, zaehler, ...abhaengig])

  return { daten, laeuft, fehler, erneut }
}
